import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, ChevronRight, Download, Loader2, Mail, Pencil, Plus, Search, Send, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { DespachoStatusBadge } from "@/components/despacho/DespachoUi";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";
import {
  despachoMensajesGeneralesApi,
  parseMensajesGeneralesError,
} from "@/modules/despacho/despachoMensajesGeneralesApi";
import {
  CAPTURE_FILING_TYPE_OPTIONS,
  CAPTURE_STATUS_OPTIONS,
  NOT_APPLICABLE_SUGGESTED_COMMENT,
  captureFilingTypeLabel,
  complianceStatusVariant,
  isCaptureNotApplicable,
} from "@/modules/despacho/complianceInstanceHelpers";
import {
  applyApplicabilityHintToDraft,
  captureObligationDisplayName,
  formatTaxResultDisplay,
  parseTaxResultAmountInput,
  regroupTaxCaptureGroupsForUi,
  TAX_RESULT_KIND_OPTIONS,
} from "@/modules/despacho/complianceCaptureHelpers";
import {
  COMPLIANCE_EVIDENCE_DOC_TYPES,
  buildShareSiblingOptions,
  complianceEvidenceDocTypeLabel,
  defaultShareSelection,
  evidenceParseHeadline,
  evidenceParseToneClass,
  formatEvidenceSize,
} from "@/modules/despacho/complianceEvidenceHelpers";
import {
  isTrackingInstanceClosed,
  monthLabelFromKey,
} from "@/modules/despacho/complianceTrackingHelpers";
import TaxSummaryWorkbookPanel from "@/components/despacho/TaxSummaryWorkbookPanel";
import { emailTransportStatusLabel, smtpPendingHint } from "@/lib/smtpPresets";

const DEFAULT_MONTH_DOCS_SUBJECT = "Envío de documentos Contables y Fiscales";

function buildDefaultMonthDocsBody({ clientName, periodKey } = {}) {
  const period = monthLabelFromKey(periodKey);
  const year = String(periodKey || "").split("-")[0] || "";
  const periodoTxt = [period, year].filter(Boolean).join(" ");
  const empresa = String(clientName || "").trim() || "su empresa";
  return (
    `Estimado(a) cliente,\n\n`
    + `Le enviamos los documentos contables y fiscales correspondientes al periodo ${periodoTxt || "indicado"} `
    + `de ${empresa}.\n\n`
    + `Quedamos a sus órdenes.\n`
  );
}

function flattenMonthDocsGrouped(grouped) {
  const decls = Array.isArray(grouped?.declaraciones) ? grouped.declaraciones : [];
  const cont = Array.isArray(grouped?.contable) ? grouped.contable : [];
  return [...decls, ...cont].filter((d) => d?.id);
}

function formatDisplayDate(value) {
  const raw = String(value || "").slice(0, 10);
  if (!raw || raw.length < 10) return "—";
  const [y, m, d] = raw.split("-");
  return `${d}/${m}/${y}`;
}

function itemIsNotApplicable(item) {
  const status = String(item?.status || "").toLowerCase();
  return status === "not_applicable_this_period" || status === "no_aplico_este_mes";
}

function deriveCaptureStatus(item) {
  if (itemIsNotApplicable(item)) return "done";
  if (isTrackingInstanceClosed(item)) return "done";
  const status = String(item?.status || "").toLowerCase();
  if (status === "in_progress" || status === "en_proceso") return "in_progress";
  if (status === "requested" || status === "solicitado") return "in_progress";
  return "pending";
}

function captureStatusLabel(value, draft) {
  if (isCaptureNotApplicable(draft?.filing_type)) return "No aplica";
  return CAPTURE_STATUS_OPTIONS.find((o) => o.value === value)?.label || "Pendiente";
}

/** Obligaciones del acordeón aún abiertas (faltan datos / no cerradas). */
function countSectionPending(items, drafts = {}) {
  let pending = 0;
  for (const item of items || []) {
    if (!item?.id) continue;
    const draft = drafts[item.id] || rowToDraft(item);
    if (isCaptureNotApplicable(draft.filing_type) || itemIsNotApplicable(item)) continue;
    if (draft.capture_status === "done" || isTrackingInstanceClosed({ ...item, ...draft })) continue;
    pending += 1;
  }
  return pending;
}

function rowToDraft(item) {
  const notApplicable = itemIsNotApplicable(item);
  return {
    capture_status: deriveCaptureStatus(item),
    filing_type: notApplicable ? "not_applicable_this_period" : (item?.filing_type || ""),
    declared_at: notApplicable ? "" : (item?.declared_at ? String(item.declared_at).slice(0, 10) : ""),
    tax_paid: notApplicable ? false : Boolean(item?.tax_paid),
    tax_result_amount: notApplicable
      ? ""
      : (item?.tax_result_amount != null && item?.tax_result_amount !== "" ? String(item.tax_result_amount) : ""),
    tax_result_kind: notApplicable ? "cargo" : (item?.tax_result_kind || "cargo"),
    client_followup_sent: notApplicable ? false : Boolean(item?.client_followup_sent),
    client_followup_sent_at: notApplicable
      ? ""
      : (item?.client_followup_sent_at ? String(item.client_followup_sent_at).slice(0, 10) : ""),
    evidence_comment: item?.evidence_comment || "",
    evidence_count: Number(item?.evidence_count) || 0,
  };
}

function NotApplicableCheckbox({ readOnly, notApplicable, onToggle }) {
  if (readOnly) {
    return <span>{notApplicable ? "✓" : "—"}</span>;
  }
  return (
    <Checkbox
      checked={notApplicable}
      onCheckedChange={(v) => onToggle(Boolean(v))}
      aria-label="No aplica este mes"
      data-testid="tracking-capture-not-applicable"
    />
  );
}

function SentToClientCell({ readOnly, notApplicable, draft, onDraftChange }) {
  if (readOnly || notApplicable) {
    return <span>{notApplicable ? "—" : (draft.client_followup_sent ? "✓" : "—")}</span>;
  }
  return (
    <div className="flex flex-col items-center gap-1">
      <Checkbox
        checked={draft.client_followup_sent}
        onCheckedChange={(v) => onDraftChange({ client_followup_sent: Boolean(v) })}
        aria-label="Enviado al cliente"
      />
      {draft.client_followup_sent ? (
        <Input
          type="date"
          className="rounded-sm h-7 text-[10px] w-[110px] px-1"
          value={draft.client_followup_sent_at}
          onChange={(e) => onDraftChange({ client_followup_sent_at: e.target.value })}
          title="Fecha de envío"
        />
      ) : null}
    </div>
  );
}

function AcceptButton({ busy, itemId, onAcceptRow }) {
  return (
    <div className="flex flex-wrap justify-end gap-1">
      <Button
        type="button"
        size="sm"
        variant="default"
        className="rounded-sm h-7 text-[10px] px-2 bg-[#0F2D5C] hover:bg-[#0a2248]"
        disabled={busy}
        onClick={onAcceptRow}
        data-testid={`tracking-capture-accept-${itemId}`}
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
        <span className="ml-1">Aceptar</span>
      </Button>
    </div>
  );
}

function ObligationEvidencePanel({
  item,
  allItems,
  readOnly,
  evidenceCount,
  onEvidenceCountChange,
  onSharedEvidence,
}) {
  const itemId = item?.id;
  const siblings = useMemo(() => buildShareSiblingOptions(item, allItems), [item, allItems]);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("provisional_normal");
  const [coverIds, setCoverIds] = useState(() => defaultShareSelection(siblings, "provisional_normal"));
  const [busyId, setBusyId] = useState(null);
  const onCountRef = useRef(onEvidenceCountChange);
  onCountRef.current = onEvidenceCountChange;
  const onSharedRef = useRef(onSharedEvidence);
  onSharedRef.current = onSharedEvidence;

  const loadItems = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    try {
      const res = await despachoFiscalInfoApi.listComplianceEvidence(itemId);
      setItems(res.items);
      onCountRef.current?.(res.count ?? res.items.length);
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    if (open) loadItems();
  }, [open, loadItems]);

  useEffect(() => {
    setCoverIds(defaultShareSelection(siblings, docType));
  }, [docType, siblings]);

  const toggleCover = (id, checked) => {
    setCoverIds((prev) => {
      const set = new Set(prev);
      if (checked) set.add(id);
      else set.delete(id);
      return Array.from(set);
    });
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || readOnly) return;
    if (!String(file.name || "").toLowerCase().endsWith(".pdf")) {
      toast.error("Solo se permiten archivos PDF.");
      return;
    }
    setUploading(true);
    try {
      const uploaded = await despachoFiscalInfoApi.uploadComplianceEvidence(itemId, {
        file,
        docType,
        alsoCoverInstanceIds: coverIds,
      });
      const parse = uploaded?.parse;
      const sharedN = Number(uploaded?.shared_count) || 0;
      if (parse?.rfc_match === false) {
        toast.warning("PDF cargado · el RFC no coincide con el cliente. Revise el archivo.");
      } else if (sharedN > 0) {
        toast.success(`PDF cargado y enlazado a ${sharedN} impuesto(s) más.`);
      } else if (parse?.summary_lines?.length) {
        toast.success(`PDF cargado · ${parse.summary_lines[0]}`);
      } else {
        toast.success("PDF de evidencia cargado.");
      }
      const sharedIds = (uploaded?.shared_to || [])
        .map((s) => s.compliance_instance_id)
        .filter(Boolean);
      if (sharedIds.length) {
        onSharedRef.current?.(sharedIds);
      }
      await loadItems();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (evidence) => {
    setBusyId(evidence.id);
    try {
      const blob = await despachoFiscalInfoApi.downloadComplianceEvidence(evidence.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = evidence.filename || "evidencia.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (evidence) => {
    if (readOnly) return;
    setBusyId(evidence.id);
    try {
      await despachoFiscalInfoApi.deleteComplianceEvidence(evidence.id);
      toast.success(
        evidence.is_shared_link
          ? "Enlace de evidencia quitado de este impuesto."
          : "Evidencia eliminada.",
      );
      await loadItems();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="border-t border-slate-100 bg-slate-50/70" data-testid={`tracking-capture-evidence-${itemId}`}>
      <button
        type="button"
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-[11px] text-slate-700 hover:bg-slate-100/80"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="inline-flex items-center gap-1.5 font-medium">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          Evidencias PDF
          <span className="text-slate-500 font-normal">
            ({evidenceCount || items.length || 0})
          </span>
        </span>
        <span className="text-[10px] text-slate-500">
          Subir declaración · puede cubrir varios impuestos
        </span>
      </button>
      {open ? (
        <div className="px-3 pb-3 space-y-2">
          {!readOnly ? (
            <div className="space-y-2 rounded-sm border border-slate-200 bg-white p-2">
              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-[200px] flex-1">
                  <label className="text-[10px] text-slate-500 block mb-1">Tipo de documento</label>
                  <Select value={docType} onValueChange={setDocType}>
                    <SelectTrigger className="rounded-sm h-8 text-xs bg-white">
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {COMPLIANCE_EVIDENCE_DOC_TYPES.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <label className="inline-flex items-center gap-1 cursor-pointer rounded-sm border border-[#0F2D5C] bg-[#0F2D5C] text-white h-8 px-2.5 text-[10px] hover:bg-[#0a2248]">
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    className="sr-only"
                    disabled={uploading}
                    onChange={handleUpload}
                  />
                  {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                  <span>Subir PDF</span>
                </label>
              </div>
              {siblings.length ? (
                <div data-testid={`tracking-evidence-also-covers-${itemId}`}>
                  <p className="text-[10px] font-medium text-slate-700 m-0 mb-1">
                    Esta declaración también cubre:
                  </p>
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {siblings.map((sib) => (
                      <label
                        key={sib.id}
                        className="inline-flex items-center gap-1.5 text-[10px] text-slate-700 cursor-pointer"
                      >
                        <Checkbox
                          checked={coverIds.includes(sib.id)}
                          onCheckedChange={(v) => toggleCover(sib.id, Boolean(v))}
                          aria-label={`Cubrir ${sib.name}`}
                        />
                        <span>{sib.name}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-[9px] text-slate-500 m-0 mt-1">
                    Marque ISR, IVA o retenciones si van en el mismo acuse. No duplica el archivo.
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
          {loading ? (
            <p className="text-[10px] text-slate-500 m-0 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Cargando evidencias…
            </p>
          ) : items.length === 0 ? (
            <p className="text-[10px] text-slate-500 m-0">
              Sin PDFs aún. Conserve aquí la declaración o acuse del mes.
            </p>
          ) : (
            <ul className="m-0 p-0 list-none space-y-1">
              {items.map((ev) => {
                const parse = ev.parse || null;
                const headline = evidenceParseHeadline(parse);
                return (
                  <li
                    key={ev.id}
                    className="rounded-sm border border-slate-200 bg-white px-2 py-1.5 text-[10px]"
                  >
                    <div className="flex flex-wrap items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="m-0 font-medium text-slate-800 truncate">
                          {ev.doc_type_label || complianceEvidenceDocTypeLabel(ev.doc_type)}
                          {ev.is_shared_link ? (
                            <span className="ml-1 font-normal text-slate-500">
                              · compartida desde {ev.shared_from_obligation_name || "otra obligación"}
                            </span>
                          ) : null}
                        </p>
                        <p className="m-0 text-slate-500 truncate">
                          {ev.filename} · {formatEvidenceSize(ev.size_bytes)}
                          {ev.created_at ? ` · ${String(ev.created_at).slice(0, 10)}` : ""}
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        disabled={busyId === ev.id}
                        onClick={() => handleDownload(ev)}
                        title="Descargar"
                        aria-label="Descargar evidencia"
                      >
                        {busyId === ev.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                      </Button>
                      {!readOnly ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-red-700 hover:text-red-800"
                          disabled={busyId === ev.id}
                          onClick={() => handleDelete(ev)}
                          title="Eliminar"
                          aria-label="Eliminar evidencia"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      ) : null}
                    </div>
                    {parse ? (
                      <div
                        className={`mt-1.5 rounded-sm border px-2 py-1.5 ${evidenceParseToneClass(parse)}`}
                        data-testid={`tracking-evidence-parse-${ev.id}`}
                      >
                        <p className="m-0 font-medium">{headline}</p>
                        {Array.isArray(parse.summary_lines) && parse.summary_lines.length ? (
                          <ul className="m-0 mt-1 pl-3 list-disc space-y-0.5">
                            {parse.summary_lines.map((line) => (
                              <li key={line}>{line}</li>
                            ))}
                          </ul>
                        ) : null}
                        {Array.isArray(parse.warnings) && parse.warnings.length ? (
                          <p className="m-0 mt-1 opacity-90">
                            {parse.warnings[0]}
                          </p>
                        ) : null}
                        <p className="m-0 mt-1 text-[9px] opacity-80">
                          Lectura automática orientativa — confirme con el PDF.
                        </p>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

function TaxCaptureTable({
  item,
  allItems,
  periodKey,
  draft,
  readOnly,
  busy,
  onDraftChange,
  onAcceptRow,
  onSharedEvidence,
}) {
  const monthLabel = monthLabelFromKey(periodKey || item?.period_key);
  const notApplicable = isCaptureNotApplicable(draft.filing_type);
  const closed = notApplicable || draft.capture_status === "done" || isTrackingInstanceClosed({ ...item, ...draft });
  const showAmount = !notApplicable && draft.tax_result_kind !== "zero";
  const colSpan = readOnly ? 10 : 11;

  const toggleNotApplicable = (checked) => {
    if (checked) {
      onDraftChange({
        filing_type: "not_applicable_this_period",
        capture_status: "done",
        declared_at: "",
        tax_paid: false,
        tax_result_amount: "",
        tax_result_kind: "cargo",
        client_followup_sent: false,
        client_followup_sent_at: "",
      });
      return;
    }
    onDraftChange({
      filing_type: "",
      capture_status: "pending",
    });
  };

  const handleFilingTypeChange = (value) => {
    if (isCaptureNotApplicable(value)) {
      toggleNotApplicable(true);
      return;
    }
    onDraftChange({ filing_type: value });
  };

  const handleTaxKindChange = (value) => {
    if (value === "zero") {
      onDraftChange({ tax_result_kind: value, tax_result_amount: "0", tax_paid: true });
      return;
    }
    onDraftChange({ tax_result_kind: value, tax_result_amount: draft.tax_result_amount === "0" ? "" : draft.tax_result_amount });
  };

  return (
    <div
      className="rounded-sm border border-slate-200 bg-white overflow-hidden"
      data-testid={`tracking-capture-obligation-${item.id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
        <p className="text-sm font-semibold text-[#0F2D5C] m-0">{captureObligationDisplayName(item)}</p>
        <DespachoStatusBadge variant={notApplicable ? "secondary" : (closed ? "success" : "secondary")}>
          {notApplicable
            ? "No aplica"
            : (item.cell_status_label || captureStatusLabel(draft.capture_status, draft))}
        </DespachoStatusBadge>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-[1220px]">
          <thead>
            <tr className="border-b border-slate-200 bg-white">
              <th className="p-2 text-left font-medium text-slate-600 w-16">Mes</th>
              <th className="p-2 text-center font-medium text-slate-600 w-16">No aplica</th>
              <th className="p-2 text-center font-medium text-slate-600 w-28 whitespace-nowrap">Enviado a Cliente</th>
              <th className="p-2 text-left font-medium text-slate-600 w-28">Fecha vence</th>
              <th className="p-2 text-left font-medium text-slate-600 w-40 whitespace-nowrap">Fecha de presentación</th>
              <th className="p-2 text-left font-medium text-slate-600 w-36">Tipo declaración</th>
              <th className="p-2 text-left font-medium text-slate-600 w-52">Monto determinado</th>
              <th className="p-2 text-center font-medium text-slate-600 w-24">Conf. pago</th>
              <th className="p-2 text-left font-medium text-slate-600 min-w-[140px]">Evidencia / texto</th>
              <th className="p-2 text-left font-medium text-slate-600 w-28">Estado</th>
              {!readOnly ? <th className="p-2 text-right font-medium text-slate-600 w-28">Acciones</th> : null}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-100">
              <td className="p-2 font-medium text-slate-800 align-middle whitespace-nowrap">{monthLabel}</td>
              <td className="p-2 text-center align-middle">
                <NotApplicableCheckbox readOnly={readOnly} notApplicable={notApplicable} onToggle={toggleNotApplicable} />
              </td>
              <td className="p-2 text-center align-middle">
                <SentToClientCell
                  readOnly={readOnly}
                  notApplicable={notApplicable}
                  draft={draft}
                  onDraftChange={onDraftChange}
                />
              </td>
              <td className="p-2 text-slate-700 whitespace-nowrap align-middle">{formatDisplayDate(item.fecha_fiscal)}</td>
              <td className="p-2 align-middle">
                {readOnly || notApplicable ? (
                  <span className="text-slate-400">{notApplicable ? "—" : formatDisplayDate(draft.declared_at)}</span>
                ) : (
                  <Input
                    type="date"
                    className="rounded-sm h-8 text-xs w-full min-w-[120px]"
                    value={draft.declared_at}
                    onChange={(e) => onDraftChange({ declared_at: e.target.value })}
                  />
                )}
              </td>
              <td className="p-2 align-middle">
                {readOnly ? (
                  <span>{captureFilingTypeLabel(draft.filing_type) || "—"}</span>
                ) : (
                  <Select
                    value={notApplicable ? "not_applicable_this_period" : (draft.filing_type || undefined)}
                    onValueChange={handleFilingTypeChange}
                    disabled={notApplicable}
                  >
                    <SelectTrigger className="rounded-sm h-8 text-xs">
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {CAPTURE_FILING_TYPE_OPTIONS.filter((o) => o.value !== "not_applicable_this_period").map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </td>
              <td className="p-2 align-middle">
                {readOnly || notApplicable ? (
                  <span className="text-slate-600 whitespace-nowrap">{notApplicable ? "—" : formatTaxResultDisplay(draft)}</span>
                ) : (
                  <div className="flex flex-row items-center gap-1.5 min-w-[180px]" data-testid="tracking-capture-monto-row">
                    <Select value={draft.tax_result_kind || "cargo"} onValueChange={handleTaxKindChange}>
                      <SelectTrigger className="rounded-sm h-8 text-xs w-[96px] shrink-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TAX_RESULT_KIND_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {showAmount ? (
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        className="rounded-sm h-8 text-xs w-[88px] shrink-0"
                        value={draft.tax_result_amount}
                        placeholder="0.00"
                        onChange={(e) => onDraftChange({ tax_result_amount: e.target.value })}
                        aria-label="Monto determinado"
                      />
                    ) : (
                      <span className="text-[10px] text-slate-500 whitespace-nowrap">Sin monto</span>
                    )}
                  </div>
                )}
              </td>
              <td className="p-2 text-center align-middle">
                {readOnly || notApplicable ? (
                  <span>{notApplicable ? "—" : (draft.tax_paid ? "✓" : "✗")}</span>
                ) : (
                  <Checkbox
                    checked={draft.tax_paid}
                    onCheckedChange={(v) => onDraftChange({ tax_paid: Boolean(v) })}
                    aria-label="Confirmación de pago"
                    disabled={draft.tax_result_kind === "zero"}
                  />
                )}
              </td>
              <td className="p-2 align-middle">
                {readOnly ? (
                  <span className="line-clamp-2">{draft.evidence_comment || "—"}</span>
                ) : (
                  <Input
                    className="rounded-sm h-8 text-xs"
                    value={draft.evidence_comment}
                    placeholder={notApplicable ? "Motivo opcional" : "Nota o texto"}
                    onChange={(e) => onDraftChange({ evidence_comment: e.target.value })}
                  />
                )}
              </td>
              <td className="p-2 align-middle">
                {readOnly ? (
                  <DespachoStatusBadge variant={complianceStatusVariant(notApplicable ? "not_applicable_this_period" : draft.capture_status)}>
                    {captureStatusLabel(draft.capture_status, draft)}
                  </DespachoStatusBadge>
                ) : (
                  <Select
                    value={notApplicable ? "done" : (draft.capture_status || "pending")}
                    onValueChange={(v) => onDraftChange({ capture_status: v })}
                    disabled={notApplicable}
                  >
                    <SelectTrigger className="rounded-sm h-8 text-xs">
                      <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                      {CAPTURE_STATUS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </td>
              {!readOnly ? (
                <td className="p-2 align-middle">
                  <AcceptButton busy={busy} itemId={item.id} onAcceptRow={onAcceptRow} />
                </td>
              ) : null}
            </tr>
            {notApplicable ? (
              <tr className="bg-slate-50/80">
                <td colSpan={colSpan} className="p-2 text-[10px] text-slate-600">
                  No hubo operación ni declaración en este mes. Pulse «Aceptar» para cerrar sin capturar fecha ni pago.
                </td>
              </tr>
            ) : null}
            {!notApplicable && (draft.filing_type === "complementaria" || item.complementaria_pending) ? (
              <tr className="bg-amber-50/60">
                <td colSpan={colSpan} className="p-2 text-[10px] text-amber-900">
                  Complementaria
                  {item.complementaria_pending ? " · impuesto pendiente de confirmar" : ""}
                  {draft.client_followup_sent && draft.client_followup_sent_at ? (
                    <span className="ml-2">· Enviado: {formatDisplayDate(draft.client_followup_sent_at)}</span>
                  ) : null}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {!notApplicable ? (
        <ObligationEvidencePanel
          item={item}
          allItems={allItems}
          readOnly={readOnly}
          evidenceCount={draft.evidence_count || 0}
          onEvidenceCountChange={(n) => onDraftChange({ evidence_count: n })}
          onSharedEvidence={onSharedEvidence}
        />
      ) : null}
    </div>
  );
}

function CaptureAccordionSection({
  sectionId,
  label,
  items,
  allItems,
  periodKey,
  drafts,
  readOnly,
  busyId,
  patchDraft,
  acceptRow,
  onSharedEvidence,
}) {
  const [open, setOpen] = useState(false);
  const count = (items || []).length;
  const pendingCount = countSectionPending(items, drafts);
  const hasPending = pendingCount > 0;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={`rounded-sm border overflow-hidden ${
        hasPending
          ? "border-rose-300 bg-rose-50 shadow-[inset_0_0_0_1px_rgba(244,63,94,0.15)]"
          : "border-slate-200 bg-white"
      }`}
      data-testid={`tracking-capture-section-${sectionId}`}
      data-pending={hasPending ? "true" : "false"}
      data-pending-count={pendingCount}
    >
      <CollapsibleTrigger
        className={`flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left ${
          hasPending ? "hover:bg-rose-100/70" : "hover:bg-slate-50/80"
        }`}
        data-testid={`tracking-capture-section-trigger-${sectionId}`}
      >
        <span className={`text-sm font-semibold ${hasPending ? "text-rose-900" : "text-[#0F2D5C]"}`}>
          {label}
        </span>
        <span className="flex items-center gap-2 shrink-0">
          {hasPending ? (
            <span
              className="rounded-sm border border-rose-300 bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-800"
              data-testid={`tracking-capture-section-pending-${sectionId}`}
            >
              {pendingCount} pendiente{pendingCount === 1 ? "" : "s"}
            </span>
          ) : null}
          <span className={`text-[11px] font-medium ${hasPending ? "text-rose-700" : "text-slate-500"}`}>
            {count === 0
              ? "Sin obligaciones aún"
              : `${count} ${count === 1 ? "obligación" : "obligaciones"}`}
          </span>
          <ChevronDown
            className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""} ${
              hasPending ? "text-rose-600" : "text-slate-500"
            }`}
            aria-hidden
          />
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className={`border-t ${hasPending ? "border-rose-200" : "border-slate-100"}`}>
        <div className={`space-y-3 p-3 ${hasPending ? "bg-rose-50/40" : ""}`}>
          {count === 0 ? (
            <p className="text-[11px] text-slate-500 m-0" data-testid={`tracking-capture-section-empty-${sectionId}`}>
              Cuando se detecte o active una obligación de este tipo, aparecerá aquí automáticamente.
            </p>
          ) : null}
          {(items || []).map((item) => {
            const draft = drafts[item.id] || rowToDraft(item);
            const itemPending = countSectionPending([item], { [item.id]: draft }) > 0;
            return (
              <div
                key={item.id}
                className={itemPending ? "rounded-sm ring-1 ring-rose-200" : undefined}
                data-pending={itemPending ? "true" : "false"}
              >
                <TaxCaptureTable
                  item={item}
                  allItems={allItems}
                  periodKey={periodKey}
                  draft={draft}
                  readOnly={readOnly}
                  busy={busyId === item.id}
                  onDraftChange={(patch) => patchDraft(item.id, patch)}
                  onAcceptRow={() => acceptRow(item)}
                  onSharedEvidence={onSharedEvidence}
                />
              </div>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function CaptureGroupSection({
  groups,
  allItems,
  periodKey,
  drafts,
  readOnly,
  busyId,
  patchDraft,
  acceptRow,
  onSharedEvidence,
}) {
  if (!groups.length) return null;
  return (
    <div className="space-y-2" data-testid="tracking-capture-tax-section">
      {groups.map((group) => {
        const sectionId = group.id || group.authority || group.label;
        return (
          <CaptureAccordionSection
            key={sectionId}
            sectionId={sectionId}
            label={group.label || group.authority || "Obligaciones"}
            items={group.items || []}
            allItems={allItems}
            periodKey={periodKey}
            drafts={drafts}
            readOnly={readOnly}
            busyId={busyId}
            patchDraft={patchDraft}
            acceptRow={acceptRow}
            onSharedEvidence={onSharedEvidence}
          />
        );
      })}
    </div>
  );
}

function MonthHonorariosBox({
  fiscalClientId,
  periodKey,
  initial,
  readOnly,
}) {
  const fromCfdi = Boolean(initial?.cobrado_desde_cfdi);
  const [cobrados, setCobrados] = useState(Boolean(initial?.honorarios_cobrados));
  const [fecha, setFecha] = useState(initial?.honorarios_cobrados_at || "");
  const [otros, setOtros] = useState(initial?.otros_cargos_nota || "");
  const [saving, setSaving] = useState(false);
  const [lockedByCfdi, setLockedByCfdi] = useState(fromCfdi);

  useEffect(() => {
    setCobrados(Boolean(initial?.honorarios_cobrados));
    setFecha(initial?.honorarios_cobrados_at || "");
    setOtros(initial?.otros_cargos_nota || "");
    setLockedByCfdi(Boolean(initial?.cobrado_desde_cfdi));
  }, [
    initial?.honorarios_cobrados,
    initial?.honorarios_cobrados_at,
    initial?.otros_cargos_nota,
    initial?.cobrado_desde_cfdi,
    fiscalClientId,
    periodKey,
  ]);

  const save = async (patch) => {
    if (readOnly || !fiscalClientId || !periodKey) return;
    setSaving(true);
    try {
      const item = await despachoFiscalInfoApi.putMonthHonorarios({
        fiscal_client_id: fiscalClientId,
        period_key: periodKey,
        ...patch,
      });
      setCobrados(Boolean(item?.honorarios_cobrados));
      setFecha(item?.honorarios_cobrados_at || "");
      setOtros(item?.otros_cargos_nota || "");
      setLockedByCfdi(Boolean(item?.cobrado_desde_cfdi));
      toast.success("Honorarios del mes guardados.");
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-sm border border-slate-200 bg-white px-3 py-2 min-w-[240px] max-w-sm"
      data-testid="tracking-month-honorarios-box"
    >
      <p className="text-[11px] font-semibold text-[#0F2D5C] m-0 mb-1.5">Honorarios del mes</p>
      {lockedByCfdi ? (
        <p
          className="text-[10px] text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-sm px-2 py-1 m-0 mb-2"
          data-testid="tracking-month-honorarios-cfdi-hint"
        >
          Cubierto según CFDI pagado (Control de honorarios). El auxiliar no necesita marcarlo.
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {readOnly || lockedByCfdi ? (
          <span className="text-[10px] text-slate-700" data-testid="tracking-month-honorarios-status">
            {cobrados ? (lockedByCfdi ? "✓ Cobrados (CFDI)" : "✓ Cobrados") : "Sin cobrar"}
            {cobrados && fecha ? ` · ${formatDisplayDate(fecha)}` : ""}
          </span>
        ) : (
          <>
            <label className="inline-flex items-center gap-1.5 text-[10px] text-slate-700 cursor-pointer">
              <Checkbox
                checked={cobrados}
                disabled={saving}
                onCheckedChange={(v) => {
                  const next = Boolean(v);
                  setCobrados(next);
                  const today = new Date().toISOString().slice(0, 10);
                  const nextFecha = next ? (fecha || today) : "";
                  setFecha(nextFecha);
                  save({
                    honorarios_cobrados: next,
                    honorarios_cobrados_at: nextFecha || null,
                    otros_cargos_nota: otros,
                  });
                }}
                aria-label="Honorarios cobrados"
                data-testid="tracking-month-honorarios-cobrados"
              />
              <span>Honorarios cobrados</span>
            </label>
            <Input
              type="date"
              className="rounded-sm h-7 text-[10px] w-[120px] px-1"
              value={fecha}
              disabled={saving || !cobrados}
              onChange={(e) => setFecha(e.target.value)}
              onBlur={() => {
                if (!cobrados) return;
                save({
                  honorarios_cobrados: true,
                  honorarios_cobrados_at: fecha || null,
                  otros_cargos_nota: otros,
                });
              }}
              title="Fecha de cobro"
            />
          </>
        )}
      </div>
      <label className="block text-[10px] text-slate-500 mb-0.5">Otros cargos al cliente</label>
      {readOnly ? (
        <p className="text-[10px] text-slate-700 m-0 whitespace-pre-wrap">{otros || "—"}</p>
      ) : (
        <textarea
          className="w-full rounded-sm border border-slate-200 text-[10px] px-2 py-1 min-h-[52px] resize-y"
          value={otros}
          disabled={saving}
          placeholder="Qué cobro adicional (asesoría, extraordinarios…) para la siguiente facturación"
          onChange={(e) => setOtros(e.target.value)}
          onBlur={() => save({
            honorarios_cobrados: cobrados,
            honorarios_cobrados_at: cobrados ? (fecha || null) : "",
            otros_cargos_nota: otros,
          })}
          data-testid="tracking-month-otros-cargos"
        />
      )}
      {saving ? (
        <p className="text-[9px] text-slate-400 m-0 mt-1 flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Guardando…
        </p>
      ) : null}
    </div>
  );
}

/** Relaciona un archivo del mes con un concepto del listado contable (por título). */
function findDocForChecklistLabel(docs, label) {
  const needle = String(label || "").trim().toLowerCase();
  if (!needle) return null;
  const list = Array.isArray(docs) ? docs : [];
  return (
    list.find((d) => String(d?.title || "").trim().toLowerCase() === needle)
    || list.find((d) => String(d?.filename || "").trim().toLowerCase().includes(needle))
    || null
  );
}

function ContablePackageChecklist({
  fiscalClientId,
  periodKey,
  initialChecklist = null,
  attachedDocs = [],
  readOnly = false,
  uploadingItemId = "",
  onChanged,
  onUploadForItem,
}) {
  const [items, setItems] = useState(Array.isArray(initialChecklist?.items) ? initialChecklist.items : []);
  const [busyId, setBusyId] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [editingId, setEditingId] = useState("");
  const [editLabel, setEditLabel] = useState("");
  const [adding, setAdding] = useState(false);
  const fileInputRefs = useRef({});

  useEffect(() => {
    setItems(Array.isArray(initialChecklist?.items) ? initialChecklist.items : []);
  }, [initialChecklist]);

  const reload = useCallback(async () => {
    if (!fiscalClientId || !periodKey) return;
    try {
      const data = await despachoFiscalInfoApi.getMonthContableChecklist({
        fiscalClientId,
        periodKey,
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
      onChanged?.(data);
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    }
  }, [fiscalClientId, periodKey, onChanged]);

  useEffect(() => {
    if (initialChecklist?.items?.length || !fiscalClientId || !periodKey) return;
    reload();
  }, [fiscalClientId, periodKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const includedCount = items.filter((it) => it.included).length;

  const handleToggle = async (item, next) => {
    if (readOnly || !item?.id) return;
    setBusyId(item.id);
    const prev = items;
    setItems((list) =>
      list.map((row) => (row.id === item.id ? { ...row, included: next } : row)),
    );
    try {
      await despachoFiscalInfoApi.toggleMonthContableChecklistItem({
        fiscalClientId,
        periodKey,
        catalogItemId: item.id,
        included: next,
      });
      onChanged?.({ items: items.map((row) => (row.id === item.id ? { ...row, included: next } : row)) });
    } catch (err) {
      setItems(prev);
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusyId("");
    }
  };

  const handleAdd = async () => {
    const label = (newLabel || "").trim();
    if (readOnly || !label) return;
    setAdding(true);
    try {
      await despachoFiscalInfoApi.createMonthContableCatalogItem({ label });
      setNewLabel("");
      toast.success("Concepto agregado al listado base.");
      await reload();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setAdding(false);
    }
  };

  const handleSaveEdit = async (itemId) => {
    const label = (editLabel || "").trim();
    if (readOnly || !itemId || !label) return;
    setBusyId(itemId);
    try {
      await despachoFiscalInfoApi.updateMonthContableCatalogItem(itemId, { label });
      setEditingId("");
      setEditLabel("");
      toast.success("Concepto actualizado.");
      await reload();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusyId("");
    }
  };

  const handleDelete = async (item) => {
    if (readOnly || !item?.id) return;
    if (!window.confirm(`¿Eliminar «${item.label}» del listado base?`)) return;
    setBusyId(item.id);
    try {
      await despachoFiscalInfoApi.deleteMonthContableCatalogItem(item.id);
      toast.success("Concepto eliminado.");
      await reload();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusyId("");
    }
  };

  return (
    <div
      className="rounded-sm border border-[#0F2D5C]/20 bg-white p-2 space-y-2"
      data-testid="tracking-month-contable-checklist"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[11px] font-semibold text-[#0F2D5C] m-0">Paquete contable (listado base)</p>
          <p className="text-[10px] text-slate-500 m-0 mt-0.5">
            Marque lo incluido este mes y use «Buscar archivo» para adjuntar evidencia por concepto.
            Puede agregar, editar o borrar conceptos del listado del despacho.
            {items.length ? ` ${includedCount}/${items.length} incluidos.` : ""}
          </p>
        </div>
      </div>

      {items.length ? (
        <ul className="space-y-1 max-h-56 overflow-y-auto m-0 p-0 list-none">
          {items.map((item) => {
            const attached = findDocForChecklistLabel(attachedDocs, item.label);
            const uploadingThis = uploadingItemId === item.id;
            return (
            <li
              key={item.id}
              className="flex items-start gap-2 text-[11px] bg-slate-50 border border-slate-100 rounded-sm px-2 py-1.5"
              data-testid={`contable-checklist-item-${item.id}`}
            >
              <Checkbox
                checked={Boolean(item.included)}
                disabled={readOnly || busyId === item.id}
                onCheckedChange={(v) => handleToggle(item, Boolean(v))}
                className="mt-0.5"
                aria-label={`Incluir ${item.label}`}
              />
              <div className="min-w-0 flex-1">
                {editingId === item.id ? (
                  <div className="flex flex-wrap items-center gap-1">
                    <Input
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      className="h-7 text-[11px] flex-1 min-w-[8rem]"
                      maxLength={160}
                      data-testid="contable-checklist-edit-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      className="h-7 px-2 text-[10px]"
                      disabled={busyId === item.id || !(editLabel || "").trim()}
                      onClick={() => handleSaveEdit(item.id)}
                    >
                      Guardar
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-[10px]"
                      onClick={() => {
                        setEditingId("");
                        setEditLabel("");
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  <>
                    <span className={item.included ? "font-medium text-slate-800" : "text-slate-600"}>
                      {item.label}
                    </span>
                    {attached ? (
                      <a
                        className="mt-0.5 block text-[10px] text-[#0F2D5C] underline truncate"
                        href={despachoFiscalInfoApi.monthClientDocumentDownloadUrl(attached.id)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`contable-checklist-attached-${item.id}`}
                      >
                        {attached.filename || attached.title}
                      </a>
                    ) : (
                      <span className="mt-0.5 block text-[10px] text-slate-400">
                        Sin archivo adjunto
                      </span>
                    )}
                  </>
                )}
              </div>
              {!readOnly && editingId !== item.id ? (
                <div className="flex shrink-0 items-center gap-0.5">
                  <input
                    ref={(el) => {
                      if (el) fileInputRefs.current[item.id] = el;
                      else delete fileInputRefs.current[item.id];
                    }}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls"
                    className="sr-only"
                    tabIndex={-1}
                    data-testid={`contable-checklist-file-${item.id}`}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (f) onUploadForItem?.(item, f);
                    }}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-1.5 text-[10px] gap-0.5 border-[#0F2D5C]/30 text-[#0F2D5C]"
                    disabled={busyId === item.id || uploadingThis || !onUploadForItem}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      fileInputRefs.current[item.id]?.click();
                    }}
                    title={`Buscar archivo para «${item.label}»`}
                    aria-label={`Buscar archivo para ${item.label}`}
                    data-testid={`contable-checklist-buscar-${item.id}`}
                  >
                    {uploadingThis ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Search className="h-3 w-3" />
                    )}
                    <span className="hidden sm:inline">Buscar</span>
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-slate-600"
                    disabled={busyId === item.id}
                    onClick={() => {
                      setEditingId(item.id);
                      setEditLabel(item.label || "");
                    }}
                    aria-label={`Editar ${item.label}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-red-700"
                    disabled={busyId === item.id}
                    onClick={() => handleDelete(item)}
                    aria-label={`Eliminar ${item.label}`}
                  >
                    {busyId === item.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              ) : null}
            </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-[11px] text-slate-500 m-0">Cargando listado base…</p>
      )}

      {!readOnly ? (
        <div className="flex flex-wrap items-end gap-1.5 pt-1 border-t border-slate-100">
          <div className="flex-1 min-w-[10rem]">
            <Label className="text-[10px] text-slate-500">Agregar concepto</Label>
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Ej. Flujo de efectivo"
              className="mt-0.5 h-8 text-[11px]"
              maxLength={160}
              data-testid="contable-checklist-add-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAdd();
                }
              }}
            />
          </div>
          <Button
            type="button"
            size="sm"
            className="h-8 gap-1 text-[11px]"
            disabled={adding || !(newLabel || "").trim()}
            onClick={handleAdd}
            data-testid="contable-checklist-add-btn"
          >
            {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Agregar
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function MonthTaxSummarySection({
  periodKey,
  fiscalClientId,
  clientName = "",
  clientEmails = [],
  monthDocuments = [],
  readOnly = false,
}) {
  const [emailOpen, setEmailOpen] = useState(false);
  const [toEmail, setToEmail] = useState("");
  const [subject, setSubject] = useState(DEFAULT_MONTH_DOCS_SUBJECT);
  const [body, setBody] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [appendFooter, setAppendFooter] = useState(true);
  const [emailConfig, setEmailConfig] = useState(null);
  const [sending, setSending] = useState(false);

  const docs = Array.isArray(monthDocuments) ? monthDocuments : [];
  const emailOptions = useMemo(() => {
    const fromClient = (clientEmails || []).map((e) => String(e || "").trim()).filter(Boolean);
    if (toEmail && !fromClient.includes(toEmail)) {
      return [toEmail, ...fromClient];
    }
    return fromClient;
  }, [clientEmails, toEmail]);

  const clientEmailsKey = (clientEmails || []).map((e) => String(e || "").trim()).filter(Boolean).join("|");

  useEffect(() => {
    setSubject(DEFAULT_MONTH_DOCS_SUBJECT);
    setBody(buildDefaultMonthDocsBody({ clientName, periodKey }));
    setSelectedIds(docs.map((d) => d.id));
    const first = clientEmailsKey ? clientEmailsKey.split("|")[0] : "";
    setToEmail(first);
  }, [periodKey, fiscalClientId, clientName, clientEmailsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Al cambiar empresa/mes, el envío por correo arranca cerrado.
  useEffect(() => {
    setEmailOpen(false);
  }, [fiscalClientId, periodKey]);

  useEffect(() => {
    const ids = new Set(docs.map((d) => d.id));
    setSelectedIds((prev) => {
      const kept = (prev || []).filter((id) => ids.has(id));
      const missing = docs.map((d) => d.id).filter((id) => !kept.includes(id));
      return [...kept, ...missing];
    });
  }, [docs]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await despachoMensajesGeneralesApi.getEmailConfig();
        if (!cancelled) setEmailConfig(cfg || null);
      } catch {
        if (!cancelled) setEmailConfig(null);
      }
    })();
    return () => { cancelled = true; };
  }, [fiscalClientId]);

  const toggleDoc = (id, checked) => {
    setSelectedIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((x) => x !== id);
    });
  };

  const handleSend = async () => {
    if (readOnly || !fiscalClientId) return;
    if (!docs.length) {
      toast.error("Cargue al menos un documento del mes antes de enviar.");
      return;
    }
    if (!selectedIds.length) {
      toast.error("Seleccione al menos un archivo para adjuntar.");
      return;
    }
    if (!toEmail.trim()) {
      toast.error("Indique el correo del cliente o captúrelo en su ficha.");
      return;
    }
    if (!subject.trim()) {
      toast.error("Indique el asunto.");
      return;
    }
    if (!body.trim()) {
      toast.error("Escriba el mensaje.");
      return;
    }
    setSending(true);
    try {
      const data = await despachoMensajesGeneralesApi.sendFreeEmail({
        fiscal_client_id: fiscalClientId,
        to_email: toEmail.trim(),
        subject: subject.trim(),
        body: body.trim(),
        append_footer: appendFooter,
        month_document_ids: selectedIds,
        dry_run: false,
      });
      const result = data.result || {};
      if (data.ok) {
        const n = Number(data.attachments_count) || selectedIds.length;
        toast.success(`Correo enviado a ${data.to_email}`, {
          description: n ? `${n} archivo(s) adjunto(s).` : undefined,
        });
      } else if (result.status === "not_configured" || data.transport_ready === false) {
        const hint = smtpPendingHint(emailConfig) || emailTransportStatusLabel(emailConfig);
        toast.error("El correo aún no puede enviarse.", {
          description: hint || "Complete Parámetros de correo en Calendario → Recordatorios.",
        });
      } else {
        toast.error(result.detail || "No se pudo enviar el correo.");
      }
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    } finally {
      setSending(false);
    }
  };

  const transportReady = Boolean(emailConfig?.transport_ready);
  const canSend = !readOnly && docs.length > 0 && selectedIds.length > 0;

  return (
    <div
      className="rounded-sm border border-slate-200 bg-white"
      data-testid="tracking-month-tax-summary"
    >
      {fiscalClientId && periodKey ? (
        <div className="border-b border-slate-100 px-3 py-2.5">
          <TaxSummaryWorkbookPanel
            fiscalClientId={fiscalClientId}
            clientName={clientName}
            periodKey={periodKey}
            readOnly={readOnly}
          />
        </div>
      ) : null}

      <Collapsible
        open={emailOpen}
        onOpenChange={setEmailOpen}
        className="border-t border-slate-100"
        data-testid="tracking-month-docs-email-form"
      >
        <CollapsibleTrigger
          className="flex w-full items-start gap-2 px-3 py-2.5 text-left hover:bg-slate-50/80"
          data-testid="tracking-month-docs-email-toggle"
        >
          <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[#0F2D5C]" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="m-0 text-sm font-semibold text-[#0F2D5C]">
              Enviar documentos del mes al cliente
            </p>
            <p className="m-0 mt-0.5 text-[11px] text-slate-500">
              {emailOpen
                ? "Usa el correo configurado del despacho (Parámetros de correo). Adjuntos: archivos cargados a la izquierda."
                : "Pulse para abrir el formulario de envío por correo."}
            </p>
          </div>
          <ChevronDown
            className={`mt-0.5 h-4 w-4 shrink-0 text-slate-500 transition-transform ${emailOpen ? "rotate-180" : ""}`}
            aria-hidden
          />
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-3 pb-2.5 space-y-2.5 border-t border-slate-100">
            {!docs.length ? (
              <p className="m-0 text-[11px] text-amber-800 bg-amber-50 border border-amber-200/80 rounded-sm px-2 py-1.5">
                Sin archivos en este mes. Cargue documentos en «Carga de documentos Contables y
                declaraciones del mes» para habilitar el envío.
              </p>
            ) : (
              <div className="space-y-1 max-h-28 overflow-y-auto rounded-sm border border-slate-200 px-2 py-1.5">
                {docs.map((doc) => {
                  const id = doc.id;
                  const checked = selectedIds.includes(id);
                  return (
                    <label
                      key={id}
                      className="flex items-start gap-2 text-[11px] text-slate-700 cursor-pointer"
                      data-testid={`tracking-month-email-doc-${id}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => toggleDoc(id, Boolean(v))}
                        disabled={readOnly || sending}
                        className="mt-0.5"
                      />
                      <span className="min-w-0">
                        <span className="font-medium block truncate">{doc.filename || "Archivo"}</span>
                        <span className="text-slate-500 block">
                          {doc.bucket_label || doc.bucket || "Documento"}
                          {doc.doc_kind_label ? ` · ${doc.doc_kind_label}` : ""}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-[11px] text-slate-600">Para</Label>
              {emailOptions.length ? (
                <Select value={toEmail || undefined} onValueChange={setToEmail} disabled={readOnly || sending}>
                  <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="tracking-month-email-to">
                    <SelectValue placeholder="Seleccione correo" />
                  </SelectTrigger>
                  <SelectContent>
                    {emailOptions.map((em) => (
                      <SelectItem key={em} value={em}>{em}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  className="h-8 text-xs rounded-sm"
                  type="email"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="correo@cliente.com"
                  disabled={readOnly || sending}
                  data-testid="tracking-month-email-to"
                />
              )}
              {!emailOptions.length ? (
                <p className="m-0 text-[10px] text-slate-500">
                  No hay correo en la ficha del cliente; escríbalo aquí o captúrelo en Clientes.
                </p>
              ) : null}
            </div>

            <div className="space-y-1">
              <Label className="text-[11px] text-slate-600">Asunto</Label>
              <Input
                className="h-8 text-xs rounded-sm"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={readOnly || sending}
                data-testid="tracking-month-email-subject"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-[11px] text-slate-600">Mensaje</Label>
              <textarea
                className="w-full min-h-[88px] rounded-sm border border-slate-200 px-2 py-1.5 text-xs text-slate-800 resize-y"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                disabled={readOnly || sending}
                data-testid="tracking-month-email-body"
              />
            </div>

            <label className="flex items-center gap-2 text-[11px] text-slate-600">
              <Checkbox
                checked={appendFooter}
                onCheckedChange={(v) => setAppendFooter(Boolean(v))}
                disabled={readOnly || sending}
              />
              Incluir pie / firma del despacho
            </label>

            {!transportReady && emailConfig ? (
              <p className="m-0 text-[10px] text-amber-800">
                {smtpPendingHint(emailConfig) || "Configure Parámetros de correo antes de enviar."}
              </p>
            ) : null}

            <Button
              type="button"
              size="sm"
              className="rounded-sm h-8 text-xs w-full sm:w-auto"
              onClick={handleSend}
              disabled={!canSend || sending}
              data-testid="tracking-month-email-send"
            >
              {sending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" aria-hidden />
              ) : (
                <Send className="h-3.5 w-3.5 mr-1.5" aria-hidden />
              )}
              Enviar al cliente
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function MonthClientDocumentsSection({
  fiscalClientId,
  periodKey,
  initialGrouped = null,
  initialContableChecklist = null,
  readOnly = false,
  onChanged,
}) {
  const [open, setOpen] = useState(false);
  const [grouped, setGrouped] = useState(initialGrouped);
  const [contableChecklist, setContableChecklist] = useState(initialContableChecklist);
  const [uploadingBucket, setUploadingBucket] = useState("");
  const [uploadingItemId, setUploadingItemId] = useState("");
  const [busyId, setBusyId] = useState("");
  const bucketFileRefs = useRef({});

  useEffect(() => {
    setGrouped(initialGrouped);
  }, [initialGrouped]);

  useEffect(() => {
    setContableChecklist(initialContableChecklist);
  }, [initialContableChecklist]);

  // Al cambiar de empresa/mes o reabrir el diálogo, arrancar cerrado (sin persistir).
  useEffect(() => {
    setOpen(false);
  }, [fiscalClientId, periodKey]);

  const reloadChecklist = useCallback(async () => {
    if (!fiscalClientId || !periodKey) return null;
    try {
      const data = await despachoFiscalInfoApi.getMonthContableChecklist({
        fiscalClientId,
        periodKey,
      });
      setContableChecklist(data);
      return data;
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
      return null;
    }
  }, [fiscalClientId, periodKey]);

  // Cargar listado base aunque la tarjeta esté cerrada (vista previa).
  useEffect(() => {
    if (!fiscalClientId || !periodKey) return;
    if (Array.isArray(initialContableChecklist?.items) && initialContableChecklist.items.length) return;
    reloadChecklist();
  }, [fiscalClientId, periodKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const reload = useCallback(async () => {
    if (!fiscalClientId || !periodKey) return;
    try {
      const data = await despachoFiscalInfoApi.listMonthClientDocuments({
        fiscalClientId,
        periodKey,
      });
      setGrouped(data);
      onChanged?.(data);
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    }
  }, [fiscalClientId, periodKey, onChanged]);

  const handleUpload = async (bucket, file) => {
    if (readOnly || !file || !fiscalClientId || !periodKey) return;
    setUploadingBucket(bucket);
    try {
      await despachoFiscalInfoApi.uploadMonthClientDocument({
        file,
        fiscalClientId,
        periodKey,
        bucket,
        title: file.name,
        readyForClient: true,
      });
      toast.success("Documento cargado. Disponible en Recordatorios → Mensajes generales.");
      await reload();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setUploadingBucket("");
    }
  };

  const handleUploadForChecklistItem = async (item, file) => {
    if (readOnly || !item?.id || !file || !fiscalClientId || !periodKey) return;
    setUploadingItemId(item.id);
    try {
      await despachoFiscalInfoApi.uploadMonthClientDocument({
        file,
        fiscalClientId,
        periodKey,
        bucket: "contable",
        title: item.label || file.name,
        readyForClient: true,
      });
      if (!item.included) {
        try {
          await despachoFiscalInfoApi.toggleMonthContableChecklistItem({
            fiscalClientId,
            periodKey,
            catalogItemId: item.id,
            included: true,
          });
        } catch {
          /* el archivo ya quedó; el check se puede marcar a mano */
        }
      }
      toast.success(`«${item.label}» cargado. Disponible para correo en Recordatorios.`);
      await Promise.all([reload(), reloadChecklist()]);
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setUploadingItemId("");
    }
  };

  const handleDelete = async (docId) => {
    if (readOnly || !docId) return;
    if (!window.confirm("¿Eliminar este documento del mes?")) return;
    setBusyId(docId);
    try {
      await despachoFiscalInfoApi.deleteMonthClientDocument(docId);
      toast.success("Documento eliminado.");
      await reload();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusyId("");
    }
  };

  const buckets = [
    { id: "declaraciones", label: "Información de declaraciones", items: grouped?.declaraciones || [] },
    { id: "contable", label: "Información contable", items: grouped?.contable || [] },
  ];
  const total = Number(grouped?.counts?.total) || 0;
  const maxFiles = Number(grouped?.max_files) || 20;
  const checklistItems = Array.isArray(contableChecklist?.items) ? contableChecklist.items : [];
  const includedCount = checklistItems.filter((it) => it.included).length;

  return (
    <div
      className={`rounded-sm border bg-white ${open ? "border-[#0F2D5C]/40 shadow-sm" : "border-slate-200"}`}
      data-testid="tracking-month-client-documents"
    >
      <button
        type="button"
        className="flex w-full items-start gap-2 px-3 py-2.5 text-left hover:bg-slate-50"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="tracking-month-client-documents-toggle"
      >
        {open ? (
          <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden />
        ) : (
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden />
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-[#0F2D5C]">
            Carga de documentos Contables y declaraciones del mes
          </span>
          <span className="mt-0.5 block text-[11px] text-slate-500">
            Paquete del periodo para correos en Recordatorios. No sustituye la evidencia PDF de cada
            impuesto. {total}/{maxFiles} archivos.
            {checklistItems.length
              ? ` Listado contable: ${includedCount}/${checklistItems.length} marcados.`
              : ""}
          </span>
        </span>
        <span className="shrink-0 text-[10px] font-medium text-[#0F2D5C] pt-0.5">
          {open ? "Cerrar" : "Abrir"}
        </span>
      </button>

      {open ? (
        <div className="border-t border-slate-100 px-3 pb-3 pt-3 space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            {buckets.map((bucket) => (
              <div
                key={bucket.id}
                className="rounded-sm border border-slate-200 bg-slate-50/80 p-2.5 space-y-2"
                data-testid={`tracking-month-docs-${bucket.id}`}
              >
                <p className="text-xs font-semibold text-slate-800 m-0">{bucket.label}</p>
                {bucket.id === "contable" ? (
                  <ContablePackageChecklist
                    fiscalClientId={fiscalClientId}
                    periodKey={periodKey}
                    initialChecklist={contableChecklist}
                    attachedDocs={bucket.items}
                    readOnly={readOnly}
                    uploadingItemId={uploadingItemId}
                    onChanged={setContableChecklist}
                    onUploadForItem={handleUploadForChecklistItem}
                  />
                ) : null}
                {bucket.items.length ? (
                  <ul className="space-y-1 max-h-36 overflow-y-auto m-0 p-0 list-none">
                    {bucket.items.map((d) => (
                      <li
                        key={d.id}
                        className="flex items-start justify-between gap-2 text-[11px] bg-white border border-slate-100 rounded-sm px-2 py-1.5"
                      >
                        <div className="min-w-0">
                          <a
                            className="text-[#0F2D5C] underline font-medium"
                            href={despachoFiscalInfoApi.monthClientDocumentDownloadUrl(d.id)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {d.title || d.filename}
                          </a>
                          <div className="text-slate-500">
                            {d.doc_kind_label}
                            {d.size_bytes ? ` · ${Math.max(1, Math.round(d.size_bytes / 1024))} KB` : ""}
                          </div>
                        </div>
                        {!readOnly ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-7 text-red-700 shrink-0 px-1"
                            disabled={busyId === d.id}
                            onClick={() => handleDelete(d.id)}
                            aria-label={`Eliminar ${d.title || d.filename}`}
                          >
                            {busyId === d.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[11px] text-slate-500 m-0">
                    {bucket.id === "contable"
                      ? "Sin archivos adjuntos (use «Buscar» en cada concepto del listado)."
                      : "Sin archivos en este grupo."}
                  </p>
                )}
                {!readOnly ? (
                  <div>
                    <Label className="text-[10px] text-slate-500">
                      {bucket.id === "contable"
                        ? "Agregar archivo extra (PDF / imagen / Excel)"
                        : "Agregar archivo (PDF / imagen / Excel)"}
                    </Label>
                    <input
                      ref={(el) => {
                        if (el) bucketFileRefs.current[bucket.id] = el;
                        else delete bucketFileRefs.current[bucket.id];
                      }}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls"
                      className="sr-only"
                      tabIndex={-1}
                      data-testid={`tracking-month-upload-${bucket.id}`}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleUpload(bucket.id, f);
                        e.target.value = "";
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="mt-1 h-8 w-full justify-center gap-1.5 text-[11px] border-[#0F2D5C]/30 text-[#0F2D5C]"
                      disabled={uploadingBucket === bucket.id || total >= maxFiles}
                      onClick={(e) => {
                        e.preventDefault();
                        bucketFileRefs.current[bucket.id]?.click();
                      }}
                      data-testid={`tracking-month-buscar-${bucket.id}`}
                    >
                      {uploadingBucket === bucket.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Search className="h-3.5 w-3.5" />
                      )}
                      {uploadingBucket === bucket.id ? "Subiendo…" : "Buscar archivo"}
                    </Button>
                    {uploadingBucket === bucket.id ? (
                      <p className="text-[10px] text-slate-500 m-0 mt-1 flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" /> Subiendo…
                      </p>
                    ) : (
                      <p className="text-[10px] text-slate-400 m-0 mt-1 flex items-center gap-1">
                        <Upload className="h-3 w-3" /> Máx. 8 MB. Puede subir varios y eliminar los que sobren.
                      </p>
                    )}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function TrackingMonthCapturePanel({
  groups = [],
  periodKey,
  fiscalClientId,
  clientName = "",
  clientEmails = [],
  monthHonorarios = null,
  monthClientDocuments = null,
  monthContableChecklist = null,
  applicabilityHints = {},
  readOnly = false,
  onUpdated,
}) {
  const taxGroups = useMemo(
    () => regroupTaxCaptureGroupsForUi(groups),
    [groups],
  );

  const allItems = useMemo(
    () => taxGroups.flatMap((g) => g.items || []),
    [taxGroups],
  );

  const [drafts, setDrafts] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [docsGrouped, setDocsGrouped] = useState(monthClientDocuments);

  useEffect(() => {
    setDocsGrouped(monthClientDocuments);
  }, [monthClientDocuments]);

  const monthDocuments = useMemo(
    () => flattenMonthDocsGrouped(docsGrouped),
    [docsGrouped],
  );

  useEffect(() => {
    const next = {};
    allItems.forEach((item) => {
      if (!item?.id) return;
      const base = rowToDraft(item);
      next[item.id] = applyApplicabilityHintToDraft(item, base, applicabilityHints);
    });
    setDrafts(next);
  }, [allItems, applicabilityHints]);

  const progressSummary = useMemo(() => {
    let closed = 0;
    let pending = 0;
    let inProgress = 0;
    let notApplicable = 0;

    allItems.forEach((item) => {
      const draft = drafts[item.id] || rowToDraft(item);
      if (isCaptureNotApplicable(draft.filing_type) || itemIsNotApplicable(item)) {
        notApplicable += 1;
        return;
      }
      if (draft.capture_status === "done" || isTrackingInstanceClosed({ ...item, ...draft })) {
        closed += 1;
        return;
      }
      if (draft.capture_status === "in_progress") {
        inProgress += 1;
        return;
      }
      pending += 1;
    });

    return {
      closed,
      pending,
      inProgress,
      notApplicable,
      total: allItems.length,
      openCount: pending + inProgress,
    };
  }, [allItems, drafts]);

  const patchDraft = useCallback((id, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), ...patch },
    }));
  }, []);

  const bumpSharedEvidenceCounts = useCallback((instanceIds) => {
    const ids = Array.isArray(instanceIds) ? instanceIds.filter(Boolean) : [];
    if (!ids.length) return;
    setDrafts((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        const cur = next[id] || {};
        next[id] = {
          ...cur,
          evidence_count: Number(cur.evidence_count || 0) + 1,
        };
      });
      return next;
    });
  }, []);

  const buildPatchBody = useCallback((draft) => {
    if (isCaptureNotApplicable(draft.filing_type)) {
      return {
        status: "not_applicable_this_period",
        comment: draft.evidence_comment?.trim() || NOT_APPLICABLE_SUGGESTED_COMMENT,
      };
    }

    const confirmFiling = draft.capture_status === "done";
    const amount = parseTaxResultAmountInput(draft.tax_result_amount);
    const body = {
      filing_type: draft.filing_type || undefined,
      declared_at: draft.declared_at || undefined,
      tax_paid: draft.tax_paid,
      client_followup_sent: draft.client_followup_sent,
      client_followup_sent_at: draft.client_followup_sent_at || undefined,
      evidence_comment: draft.evidence_comment || undefined,
      status: draft.capture_status || undefined,
      confirm_filing: confirmFiling,
      tax_result_kind: draft.tax_result_kind || undefined,
    };
    if (draft.tax_result_kind === "zero") {
      body.tax_result_amount = 0;
    } else if (amount != null) {
      body.tax_result_amount = amount;
    }
    return body;
  }, []);

  const validateDraft = useCallback((draft) => {
    if (isCaptureNotApplicable(draft.filing_type)) {
      return true;
    }
    if (draft.capture_status === "done" && (!draft.filing_type || !draft.declared_at)) {
      toast.error("Para cerrar la obligación indique tipo de declaración y fecha.");
      return false;
    }
    if (draft.tax_paid && draft.tax_result_kind === "cargo") {
      const amount = parseTaxResultAmountInput(draft.tax_result_amount);
      if (amount == null || amount <= 0) {
        toast.error("Indique el monto determinado a cargo antes de confirmar el pago.");
        return false;
      }
    }
    return true;
  }, []);

  const acceptRow = useCallback(async (item) => {
    if (readOnly || !item?.id) return;
    const draft = drafts[item.id] || rowToDraft(item);
    if (!validateDraft(draft)) return;
    setBusyId(item.id);
    try {
      await despachoFiscalInfoApi.patchComplianceInstance(item.id, buildPatchBody(draft));
      if (isCaptureNotApplicable(draft.filing_type)) {
        toast.success("Marcada como no aplica este mes.");
      } else {
        toast.success(
          draft.capture_status === "done" ? "Obligación cerrada y guardada." : "Captura guardada.",
        );
      }
      onUpdated?.();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusyId(null);
    }
  }, [readOnly, drafts, validateDraft, buildPatchBody, onUpdated]);

  return (
    <div className="space-y-4" data-testid="tracking-month-capture-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 space-y-1 flex-1 min-w-[220px]">
          <p className="m-0">
            Obligaciones del mes:{" "}
            <strong>{progressSummary.closed}</strong> cerrada{progressSummary.closed === 1 ? "" : "s"}
            {progressSummary.notApplicable > 0 ? (
              <> · <strong>{progressSummary.notApplicable}</strong> no aplica</>
            ) : null}
            {progressSummary.openCount > 0 ? (
              <>
                {" "}· <strong>{progressSummary.openCount}</strong> pendiente{progressSummary.openCount === 1 ? "" : "s"}
                {progressSummary.inProgress > 0 && progressSummary.pending > 0 ? (
                  <span className="text-slate-500"> ({progressSummary.inProgress} en progreso)</span>
                ) : null}
              </>
            ) : null}
            {" "}· <strong>{progressSummary.total}</strong> en total
          </p>
          <p className="m-0 text-[10px] text-slate-500">
            {progressSummary.total === 0
              ? "Sin obligaciones de captura en este mes. Puede cargar documentos del periodo abajo."
              : progressSummary.openCount === 0
                ? "Todas las obligaciones del mes están resueltas."
                : "Marque «No aplica» si no corresponde; el mes siguiente precarga según el cierre del mes anterior."}
          </p>
        </div>
        <MonthHonorariosBox
          fiscalClientId={fiscalClientId}
          periodKey={periodKey}
          initial={monthHonorarios}
          readOnly={readOnly}
        />
      </div>

      {fiscalClientId && periodKey ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tracking-month-top-sections">
          <MonthClientDocumentsSection
            fiscalClientId={fiscalClientId}
            periodKey={periodKey}
            initialGrouped={monthClientDocuments}
            initialContableChecklist={monthContableChecklist}
            readOnly={readOnly}
            onChanged={(data) => {
              if (
                data
                && (
                  Array.isArray(data.declaraciones)
                  || Array.isArray(data.contable)
                  || data.counts
                )
              ) {
                setDocsGrouped(data);
              }
              onUpdated?.();
            }}
          />
          <MonthTaxSummarySection
            periodKey={periodKey}
            fiscalClientId={fiscalClientId}
            clientName={clientName}
            clientEmails={clientEmails}
            monthDocuments={monthDocuments}
            readOnly={readOnly}
          />
        </div>
      ) : null}

      {allItems.length ? (
        <CaptureGroupSection
          groups={taxGroups}
          allItems={allItems}
          periodKey={periodKey}
          drafts={drafts}
          readOnly={readOnly}
          busyId={busyId}
          patchDraft={patchDraft}
          acceptRow={acceptRow}
          onSharedEvidence={bumpSharedEvidenceCounts}
        />
      ) : null}

      {!readOnly ? (
        <p className="text-[10px] text-slate-500 m-0">
          CFDI de nómina y Determinación SUA se gestionan en Recordatorios a clientes, no en esta captura.
          Impuestos requieren monto determinado (cargo/favor/en ceros) antes de confirmar pago cuando aplique.
        </p>
      ) : null}
    </div>
  );
}
